/**
 * CRM Quotation builder (P4).
 *
 * Owns the Quotes section in the prospect drawer + the builder dialog (a live
 * form on the left, an <iframe> preview of the draft render on the right) and the
 * version-history drawer. The draft is edited in place and auto-saved (debounced)
 * via PUT …/draft; the release gate is Publish (disabled while a gate is open).
 * PDF/email arrive in P6 — Print (browser print of the render URL) works today.
 */
import { escapeHtml, formatDateTimeShort } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);
const isRoot = () => hasRole('root');

const STATUS_TONE = { draft: 'off', sent: 'info', accepted: 'ok', declined: 'warn', expired: 'warn', void: 'off' };

let S = null;   // active builder state
let saveTimer = null;

// --------------------------------------------------------------------------
// Quotes section (prospect drawer)
// --------------------------------------------------------------------------

export async function mountQuotesSection(el, prospect) {
  el.innerHTML = `
    <div class="crm-detail-header">
      <strong>Quotations</strong>
      <button type="button" class="btn primary" data-q-new style="margin-left:auto; font-size:11px; padding:2px 8px;">＋ New quote</button>
    </div>
    <div class="crm-detail-body" data-role="q-list"><div class="crm-detail-loading">Loading quotes…</div></div>`;
  el.querySelector('[data-q-new]').addEventListener('click', () => newQuote(prospect, el));
  el.querySelector('[data-role="q-list"]').addEventListener('click', (ev) => onListClick(ev, prospect, el));
  await loadQuotes(el, prospect);
}

async function loadQuotes(el, prospect) {
  const list = el.querySelector('[data-role="q-list"]');
  try {
    const data = await api.fetch(API.crmProspectQuotes(prospect.id), { method: 'GET' });
    renderList(list, data.items || []);
  } catch (err) {
    list.innerHTML = '<div class="crm-detail-error">Failed to load quotes.</div>';
  }
}

function statusPill(status) {
  return `<span class="badge ${STATUS_TONE[status] || ''}">${escapeHtml(status)}</span>`;
}

function renderList(list, quotes) {
  if (!quotes.length) { list.innerHTML = '<div class="crm-detail-empty">No quotes yet.</div>'; return; }
  list.innerHTML = quotes.map((q) => {
    const ver = q.current_version_no ? `v${q.current_version_no}` : 'unpublished';
    // Approval state is a separate axis from the draft/sent lifecycle status —
    // surface it so an approved-but-unsent quote doesn't just read as "draft".
    const gates = q.gates || [];
    const unmet = q.unmet_gates || [];
    let approval = '';
    if (unmet.length) approval = ' <span class="badge warn">awaiting approval</span>';
    else if (gates.some((g) => g.status === 'approved')) approval = ' <span class="badge ok">approved</span>';
    const when = q.updated_at ? escapeHtml(formatDateTimeShort(q.updated_at)) : '';
    return `<div class="crm-quote-row" data-qid="${escapeHtml(q.id)}">
      <div class="crm-quote-main">
        <strong>${escapeHtml(q.quote_number)}</strong> ${statusPill(q.status)} <span class="crm-detail-sub">${escapeHtml(ver)}</span>${approval}
        <div class="crm-detail-sub">${escapeHtml(q.subject || '—')}${when ? ' · ' + when : ''}</div>
      </div>
      <div class="crm-quote-actions">
        <button class="btn" data-q-open>Open</button>
        <button class="btn" data-q-preview>Preview</button>
        <button class="btn" data-q-print>Print</button>
        <button class="btn" data-q-history>History</button>
      </div>
    </div>`;
  }).join('');
}

function onListClick(ev, prospect, el) {
  const row = ev.target.closest('.crm-quote-row');
  if (!row) return;
  const qid = row.dataset.qid;
  if (ev.target.closest('[data-q-open]')) openBuilder(prospect, qid, el);
  else if (ev.target.closest('[data-q-preview]')) window.open(API.crmQuoteDraftRender(qid), '_blank');
  else if (ev.target.closest('[data-q-print]')) printUrl(API.crmQuoteDraftRender(qid));
  else if (ev.target.closest('[data-q-history]')) openHistory(qid);
}

async function newQuote(prospect, el) {
  try {
    const quote = await api.fetch(API.crmProspectQuotes(prospect.id), {
      method: 'POST', body: JSON.stringify({ subject: 'New quotation', currency: 'USD' }),
    });
    await loadQuotes(el, prospect);
    openBuilder(prospect, quote.id, el);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Could not create quote', 'error');
  }
}

function printUrl(url) {
  const w = window.open(url, '_blank');
  if (w) w.addEventListener('load', () => { try { w.focus(); w.print(); } catch { /* ignore */ } });
}

// --------------------------------------------------------------------------
// Builder
// --------------------------------------------------------------------------

async function openBuilder(prospect, quoteId, sectionEl) {
  try {
    const [quote, cat, scopes] = await Promise.all([
      api.fetch(API.crmQuote(quoteId), { method: 'GET' }),
      api.fetch(API.crmQuoteCatalogue(), { method: 'GET' }).catch(() => ({ items: [] })),
      api.fetch(API.crmQuoteScopes(), { method: 'GET' }).catch(() => ({ items: [] })),
    ]);
    S = {
      prospect, sectionEl, quote,
      catalogue: cat.items || [],
      scopes: scopes.items || [],
      draft: normalizeDraft(quote, prospect),
    };
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Could not open quote', 'error');
    return;
  }
  const dlg = document.getElementById('crmQuoteBuilder');
  dlg.querySelector('[data-role="qb-title"]').textContent = `${S.quote.quote_number} — builder`;
  renderForm();
  wireBuilder(dlg);
  openDialog(dlg);
  refreshPreview();
  saveDraft(true);   // persist the seeded/normalised draft so the preview matches
}

function normalizeDraft(quote, prospect) {
  const d = quote.draft ? JSON.parse(JSON.stringify(quote.draft)) : {};
  d.subject = d.subject || quote.subject || '';
  d.currency = d.currency || quote.currency || 'USD';
  d.customer = d.customer || {
    name: prospect.full_name || '', contact: '', phone: prospect.phone_number || '',
    email: prospect.email || '', address_lines: [],
  };
  d.scope = d.scope || { lead: '', bullets: [] };
  if (!Array.isArray(d.options) || !d.options.length) {
    d.options = [{ key: 'A', title: 'Option A', path: '', badge: { text: 'BASE OFFER', style: 'base' }, lines: [] }];
  }
  d.notes = d.notes || [];
  d.prepared_by = d.prepared_by || {};
  d.payment_terms = d.payment_terms || 'monthly';
  d.mrc_display = d.mrc_display || 'annual';
  d.customer_type = d.customer_type || '';
  return d;
}

// ---- form render ----------------------------------------------------------

// The packages a line may pick — restricted to the selected scope's set so a 4G
// LTE scope can't be paired with a fibre package. No scope selected → no limit.
function scopeAllowedProductIds() {
  const tid = S.draft && S.draft.scope && S.draft.scope.template_id;
  if (!tid) return null;
  const sc = (S.scopes || []).find((x) => String(x.id) === String(tid));
  if (!sc || !(sc.product_config_ids || []).length) return null;
  return new Set(sc.product_config_ids.map(String));
}

function catalogueOptions(selectedId) {
  const allowed = scopeAllowedProductIds();
  const opts = ['<option value="">— pick a product —</option>'];
  for (const c of S.catalogue) {
    // Keep the current selection even if it's outside the scope, so nothing is lost.
    if (allowed && !allowed.has(String(c.id)) && String(c.id) !== String(selectedId)) continue;
    const on = String(c.id) === String(selectedId) ? ' selected' : '';
    const label = `${c.label || c.porta_product_code} · MRC ${c.monthly_price ?? '—'}`;
    opts.push(`<option value="${escapeHtml(c.id)}"${on}>${escapeHtml(label)}</option>`);
  }
  return opts.join('');
}

function configById(id) { return S.catalogue.find((c) => String(c.id) === String(id)); }

function money(v) { const n = Number(v); return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; }

function netOf(line) {
  const cfg = configById(line.product_config_id);
  const list = cfg ? Number(cfg.monthly_price) : Number(line.list_mrc || 0);
  const d = line.discount;
  if (!d || !d.value) return list;
  const val = Number(d.value) || 0;
  let net = list;
  switch (d.type) {
    case 'amount': case 'discount_amount': net = list - val; break;
    case 'surcharge_percent': net = list * (1 + val / 100); break;
    case 'surcharge_amount': net = list + val; break;
    default: net = list * (1 - val / 100);   // percent / discount_percent
  }
  return Math.max(0, net);
}

function renderLineRow(oi, li, line) {
  const cfg = configById(line.product_config_id);
  const list = cfg ? Number(cfg.monthly_price) : Number(line.list_mrc || 0);
  const net = netOf(line);
  const d = line.discount || {};
  const nrc = Number(line.nrc || 0);
  const chips = [];
  if (net < list) chips.push('<span class="badge warn">needs root approval</span>');   // discount only
  else if (net > list) chips.push('<span class="badge ok">surcharge</span>');
  if (nrc > 0) chips.push('<span class="badge" style="background:#f59e0b22;color:#b45309;">needs supervisor approval</span>');
  const custom = line.source === 'custom';
  const productCell = custom
    ? `<input data-line-field="title" placeholder="Custom line title" value="${escapeHtml(line.title || '')}" />
       <input data-line-field="list_mrc" type="number" step="0.01" placeholder="MRC" value="${escapeHtml(line.list_mrc || '')}" style="max-width:110px;" />`
    : `<select data-line-field="product_config_id">${catalogueOptions(line.product_config_id)}</select>
       <span class="crm-detail-sub">list MRC ${money(list)}</span>`;
  return `<div class="qb-line" data-oi="${oi}" data-li="${li}">
    <div class="qb-line-product">${productCell}</div>
    <div class="qb-line-controls">
      <label>Qty <input data-line-field="qty" type="number" min="1" value="${escapeHtml(line.qty || 1)}" style="max-width:60px;" /></label>
      <label>Adjust
        <select data-line-field="discount.type"><option value="">none</option>
          <option value="percent"${d.type === 'percent' ? ' selected' : ''}>discount %</option>
          <option value="amount"${d.type === 'amount' ? ' selected' : ''}>discount amt</option>
          <option value="surcharge_percent"${d.type === 'surcharge_percent' ? ' selected' : ''}>surcharge %</option>
          <option value="surcharge_amount"${d.type === 'surcharge_amount' ? ' selected' : ''}>surcharge amt</option></select>
        <input data-line-field="discount.value" type="number" step="0.01" value="${escapeHtml(d.value || '')}" style="max-width:80px;" />
      </label>
      <input data-line-field="discount.reason" placeholder="reason" value="${escapeHtml(d.reason || '')}" style="max-width:150px;" />
      <label>NRC <input data-line-field="nrc" type="number" step="0.01" value="${escapeHtml(line.nrc || '')}" style="max-width:90px;" /></label>
      <button class="btn ghost" data-line-del title="Remove line">✕</button>
    </div>
    <div class="qb-line-net crm-detail-sub">list ${money(list)} → <strong>net ${money(net)}</strong> ${chips.join(' ')}</div>
  </div>`;
}

function renderOption(oi, opt) {
  const badge = opt.badge || { text: 'BASE OFFER', style: 'base' };
  const lines = (opt.lines || []).map((ln, li) => renderLineRow(oi, li, ln)).join('') || '<div class="crm-detail-empty">No lines.</div>';
  const waiver = opt.waiver || null;
  return `<div class="qb-option" data-oi="${oi}">
    <div class="qb-option-head">
      <input data-opt-field="title" value="${escapeHtml(opt.title || '')}" placeholder="Option title" />
      <input data-opt-field="path" value="${escapeHtml(opt.path || '')}" placeholder="Path (A → B)" />
      <input data-opt-field="badge.text" value="${escapeHtml(badge.text || '')}" placeholder="Badge" style="max-width:120px;" />
      <select data-opt-field="badge.style"><option value="base"${badge.style === 'base' ? ' selected' : ''}>base (red)</option><option value="optional"${badge.style === 'optional' ? ' selected' : ''}>optional (dark)</option></select>
      <button class="btn ghost" data-opt-del title="Remove option">✕ option</button>
    </div>
    <div class="qb-lines">${lines}</div>
    <div class="qb-option-foot">
      <button class="btn" data-line-add>＋ line</button>
      ${isRoot() ? '<button class="btn" data-line-add-custom>＋ custom line</button>' : ''}
      <label style="margin-left:auto;"><input type="checkbox" data-opt-waiver ${waiver ? 'checked' : ''} /> NRC waiver</label>
      <input data-opt-field="waiver.text" value="${escapeHtml((waiver && waiver.text) || '')}" placeholder="waiver text" ${waiver ? '' : 'disabled'} style="flex:1; min-width:120px;" />
    </div>
  </div>`;
}

function renderForm() {
  const dlg = document.getElementById('crmQuoteBuilder');
  const form = dlg.querySelector('[data-role="qb-form"]');
  const d = S.draft;
  const c = d.customer || {};
  const scopeTpl = ['<option value="">— scope template —</option>']
    .concat(S.scopes.map((s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(s.name)}${s.product_linked ? '' : ' (custom)'}</option>`)).join('');
  form.innerHTML = `
    <div class="qb-grid">
      <label>Subject <input data-field="subject" value="${escapeHtml(d.subject || '')}" /></label>
      <label>Currency <input data-field="currency" value="${escapeHtml(d.currency || 'USD')}" maxlength="3" style="max-width:80px;" /></label>
      <label>Issue date <input data-field="issue_date" type="date" value="${escapeHtml(d.issue_date || '')}" /></label>
      <label>Valid until <input data-field="valid_until" type="date" value="${escapeHtml(d.valid_until || '')}" /></label>
      <label title="Sets the amount due up front — the monthly fee is unchanged; a prepaid term bills that many months at once.">Billing term <select data-field="payment_terms">
        ${['monthly:Monthly', 'quarterly:Quarterly (3 months)', 'semiannual:Semi-annual (6 months)', 'annual:Annual (12 months)'].map((o) => {
          const [v, t] = o.split(':');
          return `<option value="${v}"${(d.payment_terms || 'monthly') === v ? ' selected' : ''}>${t}</option>`;
        }).join('')}
      </select></label>
      <label title="How the recurring cost is summarised on the quote — the per-line MRC stays the monthly rate.">Present recurring as <select data-field="mrc_display">
        ${['monthly:Monthly', 'quarterly:Quarterly', 'semiannual:6-monthly', 'annual:Annual'].map((o) => {
          const [v, t] = o.split(':');
          return `<option value="${v}"${(d.mrc_display || 'annual') === v ? ' selected' : ''}>${t}</option>`;
        }).join('')}
      </select></label>
      <label>Customer type <select data-field="customer_type" title="Loads the matching terms &amp; conditions">
        <option value=""${!d.customer_type ? ' selected' : ''}>— all —</option>
        ${['home:Home', 'sme:SME', 'corporate:Corporate'].map((o) => {
          const [v, t] = o.split(':');
          return `<option value="${v}"${d.customer_type === v ? ' selected' : ''}>${t}</option>`;
        }).join('')}
      </select></label>
    </div>
    <fieldset class="qb-fs"><legend>Quotation for</legend>
      <div class="qb-grid">
        <label>Name <input data-cust-field="name" value="${escapeHtml(c.name || '')}" /></label>
        <label>Contact <input data-cust-field="contact" value="${escapeHtml(c.contact || '')}" /></label>
        <label>Phone <input data-cust-field="phone" value="${escapeHtml(c.phone || '')}" /></label>
        <label>Email <input data-cust-field="email" value="${escapeHtml(c.email || '')}" /></label>
      </div>
    </fieldset>
    <fieldset class="qb-fs"><legend>Scope of service</legend>
      <select data-scope-tpl>${scopeTpl}</select>
      <textarea data-scope-field="lead" rows="2" placeholder="Lead paragraph">${escapeHtml((d.scope || {}).lead || '')}</textarea>
      <div data-role="qb-bullets">${renderBullets(d.scope.bullets || [])}</div>
      <button class="btn" data-bullet-add>＋ bullet</button>
    </fieldset>
    <fieldset class="qb-fs"><legend>Options</legend>
      <div data-role="qb-options">${(d.options || []).map((o, i) => renderOption(i, o)).join('')}</div>
      <button class="btn" data-option-add>＋ option</button>
    </fieldset>
    <fieldset class="qb-fs"><legend>Notes</legend>
      <div data-role="qb-notes">${renderNotes(d.notes || [])}</div>
      <button class="btn" data-note-add>＋ note</button>
    </fieldset>`;
}

function renderBullets(bullets) {
  return bullets.map((b, i) => `<div class="qb-bullet" data-bi="${i}">
    <input data-bullet-field="label" value="${escapeHtml(b.label || '')}" placeholder="label" style="max-width:130px;" />
    <input data-bullet-field="text" value="${escapeHtml(b.text || '')}" placeholder="text" style="flex:1;" />
    <button class="btn ghost" data-bullet-del>✕</button></div>`).join('');
}

function renderNotes(notes) {
  return notes.map((n, i) => `<div class="qb-note" data-ni="${i}">
    <input data-note-field value="${escapeHtml(n || '')}" style="flex:1;" />
    <button class="btn ghost" data-note-del>✕</button></div>`).join('');
}

// ---- events + model mutation ---------------------------------------------

function wireBuilder(dlg) {
  if (dlg.dataset.wired === '1') return;
  dlg.dataset.wired = '1';
  const form = dlg.querySelector('[data-role="qb-form"]');
  form.addEventListener('input', onFormInput);
  form.addEventListener('change', onFormInput);
  form.addEventListener('click', onFormClick);
  dlg.querySelector('[data-role="qb-publish"]').addEventListener('click', doPublish);
  dlg.querySelector('[data-role="qb-print"]').addEventListener('click', () => printUrl(API.crmQuoteDraftRender(S.quote.id)));
  dlg.querySelector('[data-role="qb-pdf"]').addEventListener('click', downloadPdf);
  dlg.querySelector('[data-role="qb-email"]').addEventListener('click', emailQuote);
  // Inline gate decisions (the gatebar's innerHTML is re-rendered, but the
  // element itself persists — delegate off it).
  dlg.querySelector('[data-role="qb-gatebar"]').addEventListener('click', onGateBarClick);
}

// A user may decide a gate they hold the role for: mrc_discount → root only;
// custom_nrc → root or sales_supervisor. Self-approval is allowed (and recorded).
function canDecide(kind) {
  return kind === 'mrc_discount' ? isRoot() : (isRoot() || hasRole('sales_supervisor'));
}

async function onGateBarClick(ev) {
  const approve = ev.target.closest('[data-gate-approve]');
  const reject = ev.target.closest('[data-gate-reject]');
  if (!approve && !reject) return;
  const kind = (approve || reject).dataset.kind;
  let note = '';
  if (reject) {
    note = window.prompt('Reason for rejecting (required):', '') || '';
    if (!note.trim()) return;
  } else {
    note = window.prompt('Approval note (optional):', '') || '';
  }
  try {
    await api.fetch(API.crmQuoteDecide(S.quote.id, kind), {
      method: 'POST', body: JSON.stringify({ decision: approve ? 'approve' : 'reject', note: note || null }),
    });
    flash(approve ? 'Approved.' : 'Rejected.', 'success');
    S.quote = await api.fetch(API.crmQuote(S.quote.id), { method: 'GET' });
    updateGateBar(S.quote);
    refreshPreview();
    if (S.sectionEl) loadQuotes(S.sectionEl, S.prospect);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Decision failed', 'error');
  }
}

function downloadPdf() {
  const n = S.quote.current_version_no;
  if (!n) return;
  window.open(API.crmQuoteVersionPdf(S.quote.id, n), '_blank');
}

async function emailQuote() {
  const n = S.quote.current_version_no;
  if (!n) return;
  if (!window.confirm('Email this quote to the prospect (CC supervisor, BCC archive)?')) return;
  try {
    const res = await api.fetch(API.crmQuoteVersionSend(S.quote.id, n), { method: 'POST', body: JSON.stringify({}) });
    flash(res.pdf_attached ? 'Sent with PDF attached.' : 'Sent (link-only — no PDF renderer).', 'success');
    S.quote = await api.fetch(API.crmQuote(S.quote.id), { method: 'GET' });
    updateGateBar(S.quote);
    if (S.sectionEl) loadQuotes(S.sectionEl, S.prospect);
  } catch (err) {
    const code = err && err.body && err.body.detail;
    if (code === 'prospect_has_no_email') flash('The prospect has no email on file.', 'error');
    else if (code === 'pdf_unavailable') flash('PDF renderer unavailable on the server.', 'error');
    else flash(api.formatErrorDetail(err) || 'Send failed', 'error');
  }
}

function setPath(obj, path, value) {
  const parts = path.split('.');
  let o = obj;
  for (let i = 0; i < parts.length - 1; i++) { o[parts[i]] = o[parts[i]] || {}; o = o[parts[i]]; }
  o[parts[parts.length - 1]] = value;
}

// When a package is chosen on a line, load the scope-of-service mapped to it —
// but never overwrite a scope the officer has already filled in.
function maybeAutoScope(productId) {
  if (!productId) return false;
  const d = S.draft;
  const sc = d.scope || {};
  const hasScope = (sc.lead && sc.lead.trim()) || (Array.isArray(sc.bullets) && sc.bullets.length);
  if (hasScope) return false;
  const match = (S.scopes || []).find(
    (x) => (x.product_config_ids || []).map(String).includes(String(productId)));
  if (!match) return false;
  d.scope = { template_id: match.id, lead: match.lead || '', bullets: (match.bullets || []).slice() };
  if (match.customer_type) d.customer_type = match.customer_type;
  return true;
}

function onFormInput(ev) {
  const t = ev.target;
  const d = S.draft;
  let structural = false;
  if (t.dataset.field != null) d[t.dataset.field] = t.value;
  else if (t.dataset.custField != null) { d.customer = d.customer || {}; d.customer[t.dataset.custField] = t.value; }
  else if (t.dataset.scopeField != null) { d.scope = d.scope || {}; d.scope[t.dataset.scopeField] = t.value; }
  else if (t.dataset.bulletField != null) { const bi = +t.closest('[data-bi]').dataset.bi; d.scope.bullets[bi][t.dataset.bulletField] = t.value; }
  else if (t.hasAttribute('data-note-field')) { const ni = +t.closest('[data-ni]').dataset.ni; d.notes[ni] = t.value; }
  else if (t.dataset.optField != null) { const oi = +t.closest('[data-oi]').dataset.oi; setPath(d.options[oi], t.dataset.optField, t.value); }
  else if (t.dataset.lineField != null) {
    const row = t.closest('[data-li]'); const oi = +row.dataset.oi; const li = +row.dataset.li;
    const line = d.options[oi].lines[li];
    const field = t.dataset.lineField;
    if (field === 'product_config_id') {
      line.product_config_id = t.value || null; line.source = t.value ? 'catalog' : line.source;
      const cfg = configById(t.value);
      if (cfg) { line.title = cfg.label; if (!line.nrc && cfg.install_fee != null) line.nrc = cfg.install_fee; }
      // Load the scope-of-service mapped to this package (unless one is set).
      if (maybeAutoScope(t.value)) { renderForm(); structural = false; } else structural = true;
    } else if (field === 'discount.type') {
      line.discount = line.discount || {};
      setPath(line, field, t.value);
      if (!t.value) line.discount = null;
      structural = true;
    } else if (field === 'discount.value') {
      line.discount = line.discount || {};
      setPath(line, field, t.value);
      refreshLineMeta(oi, li);   // in-place: rebuilding the row would drop input focus
    } else {
      setPath(line, field, t.value);
      // nrc / custom list_mrc change the net + approval chips; refresh those in
      // place rather than re-rendering the row, so typing keeps focus.
      if (field === 'nrc' || field === 'list_mrc') refreshLineMeta(oi, li);
    }
  } else if (t.hasAttribute('data-opt-waiver')) {
    const oi = +t.closest('[data-oi]').dataset.oi;
    d.options[oi].waiver = t.checked ? { text: (d.options[oi].waiver || {}).text || '' } : null;
    structural = true;
  } else if (t.hasAttribute('data-scope-tpl')) {
    const sc = S.scopes.find((s) => String(s.id) === t.value);
    if (sc) {
      d.scope = { template_id: sc.id, lead: sc.lead || '', bullets: sc.bullets || [] };
      if (sc.customer_type) d.customer_type = sc.customer_type;   // picks the matching T&C set
      renderForm();
    }
    structural = true;
  } else return;

  if (structural) rerenderLive();
  scheduleSave();
}

function rerenderLive() {
  // Re-render only the options + net chips (cheap) to reflect a product/discount change.
  const dlg = document.getElementById('crmQuoteBuilder');
  const box = dlg.querySelector('[data-role="qb-options"]');
  if (box) box.innerHTML = (S.draft.options || []).map((o, i) => renderOption(i, o)).join('');
}

// Update just one line's net + approval chips, leaving its inputs untouched. Used
// while typing nrc / discount value / custom MRC so the field keeps focus.
function refreshLineMeta(oi, li) {
  const row = document.querySelector(`.qb-line[data-oi="${oi}"][data-li="${li}"]`);
  if (!row) return;
  const line = S.draft.options[oi].lines[li];
  const cfg = configById(line.product_config_id);
  const list = cfg ? Number(cfg.monthly_price) : Number(line.list_mrc || 0);
  const net = netOf(line);
  const nrc = Number(line.nrc || 0);
  const chips = [];
  if (net < list) chips.push('<span class="badge warn">needs root approval</span>');
  else if (net > list) chips.push('<span class="badge ok">surcharge</span>');
  if (nrc > 0) chips.push('<span class="badge" style="background:#f59e0b22;color:#b45309;">needs supervisor approval</span>');
  const netEl = row.querySelector('.qb-line-net');
  if (netEl) netEl.innerHTML = `list ${money(list)} → <strong>net ${money(net)}</strong> ${chips.join(' ')}`;
}

function onFormClick(ev) {
  const t = ev.target;
  const d = S.draft;
  const optEl = t.closest('[data-oi]');
  const oi = optEl ? +optEl.dataset.oi : null;
  if (t.closest('[data-bullet-add]')) { d.scope.bullets = d.scope.bullets || []; d.scope.bullets.push({ label: '', text: '' }); refreshBulletsDom(); }
  else if (t.closest('[data-bullet-del]')) { d.scope.bullets.splice(+t.closest('[data-bi]').dataset.bi, 1); refreshBulletsDom(); }
  else if (t.closest('[data-note-add]')) { d.notes.push(''); refreshNotesDom(); }
  else if (t.closest('[data-note-del]')) { d.notes.splice(+t.closest('[data-ni]').dataset.ni, 1); refreshNotesDom(); }
  else if (t.closest('[data-option-add]')) { d.options.push({ key: String.fromCharCode(65 + d.options.length), title: `Option ${d.options.length + 1}`, path: '', badge: { text: 'OPTIONAL', style: 'optional' }, lines: [] }); rerenderLive(); }
  else if (t.closest('[data-opt-del]')) { d.options.splice(oi, 1); rerenderLive(); }
  else if (t.closest('[data-line-add]')) { d.options[oi].lines.push({ source: 'catalog', product_config_id: null, qty: 1, nrc: '' }); rerenderLive(); }
  else if (t.closest('[data-line-add-custom]')) { d.options[oi].lines.push({ source: 'custom', title: '', list_mrc: '', qty: 1, nrc: '' }); rerenderLive(); }
  else if (t.closest('[data-line-del]')) { const row = t.closest('[data-li]'); d.options[oi].lines.splice(+row.dataset.li, 1); rerenderLive(); }
  else return;
  scheduleSave();
}

function refreshBulletsDom() { document.querySelector('[data-role="qb-bullets"]').innerHTML = renderBullets(S.draft.scope.bullets || []); }
function refreshNotesDom() { document.querySelector('[data-role="qb-notes"]').innerHTML = renderNotes(S.draft.notes || []); }

// ---- save / preview / publish --------------------------------------------

function scheduleSave() { clearTimeout(saveTimer); saveTimer = setTimeout(() => saveDraft(false), 500); }

async function saveDraft(silent) {
  if (!S) return;
  try {
    const updated = await api.fetch(API.crmQuoteDraft(S.quote.id), {
      method: 'PUT', body: JSON.stringify({ draft: S.draft }),
    });
    S.quote = updated;
    updateGateBar(updated);
    refreshPreview();
    if (S.sectionEl) loadQuotes(S.sectionEl, S.prospect);
  } catch (err) {
    updateGateBar(null, api.formatErrorDetail(err) || 'Save failed');
    if (!silent) flash(api.formatErrorDetail(err) || 'Save failed', 'error');
  }
}

function refreshPreview() {
  const iframe = document.getElementById('crmQuotePreview');
  if (iframe) iframe.src = `${API.crmQuoteDraftRender(S.quote.id)}?t=${Date.now()}`;
}

const GATE_LABEL = { mrc_discount: 'Discount → root', custom_nrc: 'Installation NRC → supervisor' };
const GATE_REASON = {
  mrc_discount: 'a monthly discount needs root approval',
  custom_nrc: 'an installation charge (NRC) needs sales-supervisor approval',
};

function updateGateBar(quote, errorMsg) {
  const dlg = document.getElementById('crmQuoteBuilder');
  const bar = dlg.querySelector('[data-role="qb-gatebar"]');
  const publishBtn = dlg.querySelector('[data-role="qb-publish"]');
  if (errorMsg) { bar.innerHTML = `<span class="badge warn">${escapeHtml(errorMsg)}</span>`; return; }
  const gates = (quote && quote.gates) || [];
  const unmet = (quote && quote.unmet_gates) || [];
  const blocked = unmet.length > 0;
  publishBtn.disabled = blocked;
  publishBtn.title = blocked ? 'Blocked — a release gate is awaiting approval' : 'Publish this version';
  // PDF / Email act on a published version.
  const published = !!(quote && quote.current_version_no);
  const pdfBtn = dlg.querySelector('[data-role="qb-pdf"]');
  const emailBtn = dlg.querySelector('[data-role="qb-email"]');
  [pdfBtn, emailBtn].forEach((b) => { if (b) { b.disabled = !published; b.title = published ? '' : 'Publish a version first'; } });

  const rows = gates.map((g) => {
    const label = GATE_LABEL[g.kind] || g.kind;
    const tone = g.status === 'approved' ? 'ok' : (g.status === 'rejected' ? 'warn' : 'off');
    const note = g.decision_note ? ` · <span class="crm-detail-sub">${escapeHtml(g.decision_note)}</span>` : '';
    const actions = (g.status === 'pending' && canDecide(g.kind))
      ? ` <button class="btn" data-gate-approve data-kind="${escapeHtml(g.kind)}" style="padding:1px 8px;font-size:11px;">Approve</button>`
        + ` <button class="btn ghost" data-gate-reject data-kind="${escapeHtml(g.kind)}" style="padding:1px 8px;font-size:11px;">Reject</button>`
      : '';
    return `<div>${escapeHtml(label)} <span class="badge ${tone}">${escapeHtml(g.status)}</span>${note}${actions}</div>`;
  }).join('');

  // Explain *why* Publish is disabled so the author isn't left guessing.
  if (blocked) {
    const reasons = unmet.map((k) => `<li>${escapeHtml(GATE_REASON[k] || k)}</li>`).join('');
    bar.innerHTML = `
      <div style="border:1px solid var(--border); border-left:3px solid #d97706; border-radius:4px; padding:6px 10px; margin-bottom:6px;">
        <strong>Publish is disabled — awaiting approval:</strong>
        <ul style="margin:4px 0; padding-left:18px;">${reasons}</ul>
        <span class="crm-detail-sub">A root user or sales supervisor approves these under <em>Quote Approvals</em>. The quote stays a draft until then.</span>
      </div>${rows}`;
    return;
  }
  if (!gates.length) { bar.innerHTML = '<span class="crm-detail-sub">No approvals required — ready to publish.</span>'; return; }
  bar.innerHTML = `<div class="crm-detail-sub" style="margin-bottom:2px;">All approvals cleared — ready to publish.</div>${rows}`;
}

async function doPublish() {
  try {
    const updated = await api.fetch(API.crmQuotePublish(S.quote.id), { method: 'POST', body: JSON.stringify({}) });
    S.quote = updated;
    flash(`Published v${updated.current_version_no}`, 'success');
    updateGateBar(updated);
    if (S.sectionEl) loadQuotes(S.sectionEl, S.prospect);
  } catch (err) {
    if (err && err.status === 409) flash('Cannot publish — a release gate is still open.', 'error');
    else flash(api.formatErrorDetail(err) || 'Publish failed', 'error');
  }
}

// --------------------------------------------------------------------------
// History drawer
// --------------------------------------------------------------------------

async function openHistory(quoteId) {
  const dlg = document.getElementById('crmQuoteHistory');
  const body = dlg.querySelector('[data-role="qh-body"]');
  body.innerHTML = '<div class="crm-detail-loading">Loading…</div>';
  openDialog(dlg);
  try {
    const data = await api.fetch(API.crmQuoteVersions(quoteId), { method: 'GET' });
    const items = data.items || [];
    body.innerHTML = items.length ? items.slice().reverse().map((v) => `
      <div class="crm-quote-row">
        <div class="crm-quote-main"><strong>v${v.version_no}</strong>
          <div class="crm-detail-sub">${v.created_at ? escapeHtml(formatDateTimeShort(v.created_at)) : ''}${v.change_note ? ' · ' + escapeHtml(v.change_note) : ''}</div></div>
        <div class="crm-quote-actions">
          <button class="btn" data-v-preview="${v.version_no}">Preview</button>
          <button class="btn" data-v-print="${v.version_no}">Print</button>
        </div>
      </div>`).join('') : '<div class="crm-detail-empty">No published versions.</div>';
    body.onclick = (ev) => {
      const p = ev.target.closest('[data-v-preview]'); const pr = ev.target.closest('[data-v-print]');
      if (p) window.open(API.crmQuoteVersionRender(quoteId, p.dataset.vPreview), '_blank');
      else if (pr) printUrl(API.crmQuoteVersionRender(quoteId, pr.dataset.vPrint));
    };
  } catch (err) {
    body.innerHTML = '<div class="crm-detail-error">Failed to load history.</div>';
  }
}

// --------------------------------------------------------------------------
// Approvals inbox (CRM Tools → Quote approvals)
// --------------------------------------------------------------------------

const KIND_LABEL = { mrc_discount: 'Discount → root', custom_nrc: 'Installation NRC → supervisor' };

function ageOf(iso) {
  if (!iso) return '';
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3.6e6);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function deltaHtml(item) {
  const lines = item.delta || [];
  if (!lines.length) return '<span class="crm-detail-sub">—</span>';
  if (item.kind === 'mrc_discount') {
    return lines.map((l) => {
      const d = l.discount || {};
      const dv = d.value ? ` (−${escapeHtml(d.value)}${d.type === 'percent' ? '%' : ''})` : '';
      return `<div>${escapeHtml(l.title || 'line')}: <span class="crm-detail-sub">list ${escapeHtml(l.list_mrc)}</span> → <strong>net ${escapeHtml(l.net_mrc)}</strong>${dv}</div>`;
    }).join('');
  }
  return lines.map((l) => `<div>${escapeHtml(l.title || 'line')}: <strong>NRC ${escapeHtml(l.nrc)}</strong></div>`).join('');
}

const _inbox = { status: 'pending', q: '', decided_by: '', delivery: '', sort: 'date', offset: 0, limit: 25 };
let _inboxSearchTimer = null;

export async function mountInbox(container) {
  container = container || document.getElementById('crmQuoteApprovalsView');
  if (!container) return;
  Object.assign(_inbox, { status: 'pending', q: '', decided_by: '', delivery: '', sort: 'date', offset: 0, limit: 25 });
  const opt = (v, t, sel) => `<option value="${v}"${sel === v ? ' selected' : ''}>${t}</option>`;
  container.innerHTML = `
    <div class="crm-detail-header" style="margin-bottom:8px;">
      <strong style="font-size:15px;">Quote approvals</strong>
      <span class="crm-detail-sub" style="margin-left:8px;" data-role="inbox-scope">Pending release gates</span>
      <button class="btn ghost" data-inbox-refresh style="margin-left:auto;">↻ Refresh</button>
    </div>
    <div class="row" style="gap:6px; margin-bottom:10px; flex-wrap:wrap; align-items:flex-end;">
      <input data-inbox-q class="input" type="search" placeholder="Search quote № or customer…" style="min-width:200px;">
      <label class="field" style="margin:0;"><span class="crm-detail-sub">Status</span>
        <select data-inbox-status class="input">
          ${opt('pending', 'Pending', _inbox.status)}${opt('approved', 'Approved', _inbox.status)}
          ${opt('rejected', 'Rejected', _inbox.status)}${opt('withdrawn', 'Withdrawn', _inbox.status)}
          ${opt('all', 'All', _inbox.status)}
        </select></label>
      <label class="field" style="margin:0;"><span class="crm-detail-sub">Approved by</span>
        <select data-inbox-decider class="input"><option value="">Anyone</option></select></label>
      <label class="field" style="margin:0;"><span class="crm-detail-sub">Outreach</span>
        <select data-inbox-delivery class="input">
          <option value="">Any</option>
          ${opt('emailed', 'Emailed', _inbox.delivery)}${opt('downloaded', 'Downloaded', _inbox.delivery)}
          ${opt('any', 'Emailed or downloaded', _inbox.delivery)}${opt('none', 'Not sent', _inbox.delivery)}
        </select></label>
      <label class="field" style="margin:0;"><span class="crm-detail-sub">Sort</span>
        <select data-inbox-sort class="input">${opt('date', 'Newest first', _inbox.sort)}${opt('name', 'Name', _inbox.sort)}</select></label>
    </div>
    <div class="table-wrap"><table class="crm-inbox-table">
      <thead><tr><th>Customer</th><th>Quote</th><th>Gate</th><th>Delta</th><th>Requested</th><th>Decision</th><th></th></tr></thead>
      <tbody data-role="inbox-body"><tr><td colspan="7" class="crm-detail-loading">Loading…</td></tr></tbody>
    </table></div>
    <div class="row" data-role="inbox-pager" style="gap:8px; margin-top:8px; align-items:center; justify-content:flex-end; font-size:12px;"></div>`;

  container.querySelector('[data-inbox-refresh]').addEventListener('click', () => loadInbox(container));
  container.querySelector('[data-role="inbox-body"]').addEventListener('click', (ev) => onInboxClick(ev, container));
  container.querySelector('[data-role="inbox-pager"]').addEventListener('click', (ev) => onPagerClick(ev, container));
  container.querySelector('[data-inbox-q]').addEventListener('input', (ev) => {
    _inbox.q = ev.target.value; _inbox.offset = 0;
    clearTimeout(_inboxSearchTimer);
    _inboxSearchTimer = setTimeout(() => loadInbox(container), 250);
  });
  ['status', 'decider', 'delivery', 'sort'].forEach((k) => {
    const key = k === 'decider' ? 'decided_by' : k;
    container.querySelector(`[data-inbox-${k}]`).addEventListener('change', (ev) => {
      _inbox[key] = ev.target.value; _inbox.offset = 0; loadInbox(container);
    });
  });
  await loadInbox(container);
}

function _inboxParams() {
  const p = new URLSearchParams();
  p.set('status', _inbox.status);
  if (_inbox.q) p.set('q', _inbox.q);
  if (_inbox.decided_by) p.set('decided_by', _inbox.decided_by);
  if (_inbox.delivery) p.set('delivery', _inbox.delivery);
  p.set('sort', _inbox.sort);
  p.set('limit', String(_inbox.limit));
  p.set('offset', String(_inbox.offset));
  return p.toString();
}

async function loadInbox(container) {
  const body = container.querySelector('[data-role="inbox-body"]');
  const scope = container.querySelector('[data-role="inbox-scope"]');
  if (scope) scope.textContent = _inbox.status === 'pending' ? 'Pending release gates' : `${_inbox.status[0].toUpperCase()}${_inbox.status.slice(1)} approvals`;
  try {
    const data = await api.fetch(API.crmQuoteApprovalsInbox(_inboxParams()), { method: 'GET' });
    renderInbox(body, data.items || []);
    renderDeciders(container, data.deciders || []);
    renderPager(container, data.total || 0, (data.items || []).length);
    if (_inbox.status === 'pending' && !_inbox.q && !_inbox.decided_by && !_inbox.delivery && _inbox.offset === 0) {
      setInboxBadge(data.total || 0);
    }
  } catch (err) {
    body.innerHTML = '<tr><td colspan="7" class="crm-detail-error">Failed to load approvals.</td></tr>';
  }
}

// Keep the approver dropdown populated (once we know the deciders), preserving choice.
function renderDeciders(container, deciders) {
  const sel = container.querySelector('[data-inbox-decider]');
  if (!sel) return;
  const opts = ['<option value="">Anyone</option>'].concat(
    deciders.map((d) => `<option value="${escapeHtml(String(d.id))}">${escapeHtml(d.name)}</option>`));
  sel.innerHTML = opts.join('');
  sel.value = _inbox.decided_by || '';
}

function renderPager(container, total, shown) {
  const el = container.querySelector('[data-role="inbox-pager"]');
  if (!el) return;
  if (!total) { el.innerHTML = ''; return; }
  const from = total ? _inbox.offset + 1 : 0;
  const to = _inbox.offset + shown;
  const prevDis = _inbox.offset <= 0 ? 'disabled' : '';
  const nextDis = to >= total ? 'disabled' : '';
  el.innerHTML = `<span class="crm-detail-sub">${from}–${to} of ${total}</span>
    <button class="btn ghost" data-inbox-prev ${prevDis}>‹ Prev</button>
    <button class="btn ghost" data-inbox-next ${nextDis}>Next ›</button>`;
}

function onPagerClick(ev, container) {
  if (ev.target.closest('[data-inbox-prev]')) { _inbox.offset = Math.max(0, _inbox.offset - _inbox.limit); loadInbox(container); }
  else if (ev.target.closest('[data-inbox-next]')) { _inbox.offset += _inbox.limit; loadInbox(container); }
}

function _outreachBadges(it) {
  const b = [];
  if (it.emailed) b.push('<span class="badge ok" title="Emailed to the customer">✉ Emailed</span>');
  if (it.downloaded) b.push('<span class="badge" title="Its PDF was downloaded">⬇ Downloaded</span>');
  return b.length ? ` ${b.join(' ')}` : '';
}

function renderInbox(body, items) {
  if (!items.length) { body.innerHTML = '<tr><td colspan="7" class="crm-detail-empty">No approvals match. ✅</td></tr>'; return; }
  body.innerHTML = items.map((it) => {
    const ver = it.current_version_no ? `v${it.current_version_no}` : 'unpublished';
    const self = it.self_approved ? ' <span class="badge warn" title="Self-approved — flagged for audit">self</span>' : '';
    const pending = it.status === 'pending';
    const decisionCls = { approved: 'ok', rejected: 'danger', withdrawn: 'muted' }[it.status] || '';
    const decision = pending
      ? '<span class="badge warn">Pending</span>'
      : `<span class="badge ${decisionCls}">${escapeHtml(it.status)}</span>`
        + (it.decided_by_name ? `<div class="crm-detail-sub">by ${escapeHtml(it.decided_by_name)} · ${escapeHtml(ageOf(it.decided_at))}</div>` : '');
    const actions = pending
      ? '<button class="btn" data-inbox-preview>Preview</button> <button class="btn" data-inbox-edit title="Open the quote builder to revise it before deciding">Edit</button> <button class="btn primary" data-inbox-approve>Approve</button> <button class="btn ghost" data-inbox-reject>Reject</button>'
      : '<button class="btn" data-inbox-preview>Preview</button>';
    return `<tr data-qid="${escapeHtml(it.quote_id)}" data-kind="${escapeHtml(it.kind)}" data-vn="${it.current_version_no || ''}" data-pid="${escapeHtml(it.prospect_id || '')}" data-pname="${escapeHtml(it.prospect_name || '')}">
      <td>${escapeHtml(it.prospect_name || '—')}</td>
      <td><strong>${escapeHtml(it.quote_number || '—')}</strong><div class="crm-detail-sub">${escapeHtml(it.quote_subject || '')} · ${escapeHtml(ver)}${_outreachBadges(it)}</div></td>
      <td>${escapeHtml(KIND_LABEL[it.kind] || it.kind)}${self}</td>
      <td style="font-size:12px;">${deltaHtml(it)}</td>
      <td class="crm-detail-sub">${escapeHtml(ageOf(it.requested_at))}${it.requested_by_name ? ' · ' + escapeHtml(it.requested_by_name) : ''}</td>
      <td>${decision}</td>
      <td style="white-space:nowrap;">${actions}</td></tr>`;
  }).join('');
}

async function onInboxClick(ev, container) {
  const row = ev.target.closest('tr[data-qid]');
  if (!row) return;
  const qid = row.dataset.qid; const kind = row.dataset.kind;
  if (ev.target.closest('[data-inbox-preview]')) {
    const vn = row.dataset.vn;
    const url = vn ? API.crmQuoteVersionRender(qid, vn) : API.crmQuoteDraftRender(qid);
    window.open(url, '_blank');
    return;
  }
  if (ev.target.closest('[data-inbox-edit]')) {
    // Open the same builder the owner uses, so an approver can revise before
    // deciding (edits recompute the gates); reload the inbox when it closes.
    const prospect = { id: row.dataset.pid || null, full_name: row.dataset.pname || '' };
    await openBuilder(prospect, qid, null);
    const dlg = document.getElementById('crmQuoteBuilder');
    if (dlg) dlg.addEventListener('close', () => loadInbox(container), { once: true });
    return;
  }
  const approve = ev.target.closest('[data-inbox-approve]');
  const reject = ev.target.closest('[data-inbox-reject]');
  if (!approve && !reject) return;
  let note = '';
  if (reject) {
    note = window.prompt('Reason for rejecting (required):', '') || '';
    if (!note.trim()) return;
  } else {
    note = window.prompt('Approval note (optional):', '') || '';
  }
  try {
    await api.fetch(API.crmQuoteDecide(qid, kind), {
      method: 'POST', body: JSON.stringify({ decision: approve ? 'approve' : 'reject', note: note || null }),
    });
    flash(approve ? 'Approved.' : 'Rejected.', 'success');
    await loadInbox(container);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Decision failed', 'error');
  }
}

// The nav badge (attention chip) — pending count on the CRM tools nav item.
function setInboxBadge(n) {
  const badge = document.getElementById('crmQuoteApprovalsBadge');
  if (!badge) return;
  badge.textContent = n > 0 ? String(n) : '';
  badge.style.display = n > 0 ? '' : 'none';
}

export async function refreshInboxBadge() {
  try {
    const data = await api.fetch(API.crmQuoteApprovalsInbox('status=pending&limit=1'), { method: 'GET' });
    setInboxBadge(data.total != null ? data.total : (data.items || []).length);
  } catch (err) { /* non-fatal */ }
}

// --------------------------------------------------------------------------
// Root config (CRM Installation Config → Quotations)
// --------------------------------------------------------------------------

let Cfg = null;

export async function mountConfig(container) {
  container = container || document.getElementById('crmQuoteConfig');
  if (!container) return;
  const root = hasRole('root');
  container.innerHTML = '<div class="crm-detail-loading">Loading quotation config…</div>';
  try {
    const [settings, scopes, cat] = await Promise.all([
      root ? api.fetch(API.crmQuoteSettings(), { method: 'GET' }) : Promise.resolve(null),
      api.fetch(API.crmQuoteScopes(), { method: 'GET' }).catch(() => ({ items: [] })),
      api.fetch(API.crmQuoteCatalogue(), { method: 'GET' }).catch(() => ({ items: [] })),
    ]);
    Cfg = { container, root, settings: settings || {}, scopes: scopes.items || [], catalogue: cat.items || [], scopeEditId: null };
    renderConfig();
  } catch (err) {
    container.innerHTML = '<div class="crm-detail-error">Failed to load quotation config.</div>';
  }
}

function scopeProductOptions(selectedIds) {
  const sel = new Set((selectedIds || []).map(String));
  return Cfg.catalogue.map((c) => {
    const label = `${c.porta_product_code} · ${c.label || ''}${c.customer_type ? ' (' + c.customer_type + ')' : ''}`;
    return `<option value="${escapeHtml(c.id)}"${sel.has(String(c.id)) ? ' selected' : ''}>${escapeHtml(label)}</option>`;
  }).join('');
}

function renderConfig() {
  const s = Cfg.settings || {};
  const lh = s.letterhead || {};
  const notes = (s.default_notes || []).join('\n');
  const terms = s.terms || [];
  const settingsBlock = Cfg.root ? `
    <fieldset class="qb-fs"><legend>Letterhead &amp; document</legend>
      <div class="qb-grid">
        <label>Company name <input data-cfg="lh.name" value="${escapeHtml(lh.name || '')}" /></label>
        <label>Website <input data-cfg="lh.website" value="${escapeHtml(lh.website || '')}" /></label>
        <label>Phone label <input data-cfg="lh.phone_label" value="${escapeHtml(lh.phone_label || 'Tel')}" /></label>
        <label>Phone <input data-cfg="lh.phone" value="${escapeHtml(lh.phone || '')}" /></label>
        <label>Email <input data-cfg="lh.email" value="${escapeHtml(lh.email || '')}" /></label>
        <label>Footer line <input data-cfg="footer_line" value="${escapeHtml(s.footer_line || '')}" /></label>
      </div>
      <label>Address lines (one per line)<textarea data-cfg="lh.address_lines" rows="2">${escapeHtml((lh.address_lines || []).join('\n'))}</textarea></label>
      <div class="qb-grid">
        <label>Validity (days) <input data-cfg="validity_days" type="number" value="${escapeHtml(s.validity_days ?? 30)}" /></label>
        <label>Number prefix <input data-cfg="number_prefix" value="${escapeHtml(s.number_prefix || 'INET/QT')}" /></label>
      </div>
      <p class="crm-detail-sub" style="margin:2px 0 0;">Quote emails automatically CC all sales supervisors and BCC root — no setup needed.</p>
      <label>Default notes (one per line)<textarea data-cfg="default_notes" rows="2">${escapeHtml(notes)}</textarea></label>
      <label>Logo <input type="file" accept="image/*" data-cfg-logo />
        <span class="crm-detail-sub" data-role="logo-state">${s.has_logo ? 'a logo is set' : 'no logo'}</span></label>
    </fieldset>
    <fieldset class="qb-fs"><legend>Invoicing — office contact</legend>
      <p class="crm-detail-sub" style="margin:0 0 6px;">Shown on invoices &amp; receipts as the direct line of contact when there is no issuing officer (e.g. auto-generated receipts). Officer-generated invoices show that officer's name and email, with this number as the general line.</p>
      <div class="qb-grid">
        <label>Office email <input data-cfg="office_email" value="${escapeHtml(s.office_email || '')}" /></label>
        <label>Office phone <input data-cfg="office_phone" value="${escapeHtml(s.office_phone || '')}" /></label>
      </div>
    </fieldset>
    <fieldset class="qb-fs"><legend>Terms &amp; conditions (${terms.length})</legend>
      <p class="crm-detail-sub" style="margin:0 0 6px;">Tag clauses by customer type so the right set loads per quote. Variables you can use in a clause: <code>{{validity_days}}</code>, <code>{{valid_until}}</code>, <code>{{issue_date}}</code>, <code>{{quote_number}}</code>.</p>
      <div data-role="cfg-terms">${renderTerms(terms)}</div>
      <button class="btn" data-term-add>＋ clause</button>
    </fieldset>
    <div style="margin:10px 0;"><button class="btn primary" data-cfg-save>Save settings &amp; terms</button></div>` : '';

  Cfg.container.innerHTML = `
    <div class="crm-detail-header" style="margin-bottom:10px;"><strong style="font-size:15px;">Quotations</strong>
      <span class="crm-detail-sub" style="margin-left:8px;">${Cfg.root ? 'Letterhead, terms and scope library' : 'Scope library'}</span></div>
    ${settingsBlock}
    <fieldset class="qb-fs"><legend>Scope-of-service library</legend>
      <div class="table-wrap"><table class="crm-inbox-table"><thead><tr><th>Name</th><th>Type</th><th>Packages</th><th>Active</th><th></th></tr></thead>
        <tbody data-role="cfg-scopes">${renderScopes(Cfg.scopes)}</tbody></table></div>
      <div data-role="cfg-scope-form" style="margin-top:10px;">${renderScopeForm()}</div>
    </fieldset>`;
  wireConfig();
}

const TERM_CTYPES = [['home', 'Home'], ['sme', 'SME'], ['corporate', 'Corporate']];

function renderTerms(terms) {
  return terms.map((c, i) => {
    const sel = (c.customer_types || []).map((x) => String(x).toLowerCase());
    const checks = TERM_CTYPES.map(([v, label]) =>
      `<label style="font-weight:400; font-size:11px;"><input type="checkbox" data-term-ctype="${v}"${sel.includes(v) ? ' checked' : ''} /> ${label}</label>`).join(' ');
    return `<div class="qb-term" data-ti="${i}">
    <div style="display:flex; gap:6px; align-items:center;">
      <input data-term-field="title" value="${escapeHtml(c.title || '')}" placeholder="Clause title" style="flex:1;" />
      <button class="btn ghost" data-term-up title="Move up">↑</button>
      <button class="btn ghost" data-term-down title="Move down">↓</button>
      <button class="btn ghost" data-term-del title="Remove">✕</button>
    </div>
    <textarea data-term-field="body" rows="2" placeholder="Clause body" style="width:100%; margin-top:4px;">${escapeHtml(c.body || '')}</textarea>
    <div class="crm-detail-sub" style="margin-top:3px;">Applies to: ${checks} <span style="opacity:.65;">— none ticked = all types</span></div>
  </div>`;
  }).join('');
}

function renderScopes(scopes) {
  if (!scopes.length) return '<tr><td colspan="5" class="crm-detail-empty">No scope templates.</td></tr>';
  return scopes.map((s) => {
    const n = (s.product_config_ids || []).length;
    const pkgs = n ? `${n} package${n === 1 ? '' : 's'}` : '<span class="badge warn">custom</span>';
    return `<tr data-sid="${escapeHtml(s.id)}">
    <td>${escapeHtml(s.name)}</td><td>${escapeHtml(s.customer_type || '—')}</td>
    <td>${pkgs}</td>
    <td>${s.is_active ? '✓' : '—'}</td>
    <td style="white-space:nowrap;"><button class="btn" data-scope-edit>Edit</button>
      ${s.is_active ? '<button class="btn ghost" data-scope-deactivate>Deactivate</button>' : '<button class="btn ghost" data-scope-activate>Activate</button>'}</td></tr>`;
  }).join('');
}

function renderScopeForm() {
  const editing = Cfg.scopeEditId;
  const s = editing ? Cfg.scopes.find((x) => x.id === editing) || {} : {};
  const bullets = (s.bullets || []).map((b) => `${b.label || ''} | ${b.text || ''}`).join('\n');
  return `<div class="qb-fs" style="margin:0;">
    <strong style="font-size:13px;">${editing ? 'Edit scope' : 'New scope'}</strong>
    <div class="qb-grid">
      <label>Name <input data-scopef="name" value="${escapeHtml(s.name || '')}" /></label>
      <label>Customer type <select data-scopef="customer_type">
        <option value="">—</option><option value="corporate"${s.customer_type === 'corporate' ? ' selected' : ''}>corporate</option>
        <option value="sme"${s.customer_type === 'sme' ? ' selected' : ''}>sme</option><option value="home"${s.customer_type === 'home' ? ' selected' : ''}>home</option></select></label>
    </div>
    <label>Packages this scope applies to <span class="crm-detail-sub">(Ctrl/Cmd-click for multiple${Cfg.root ? '' : '; at least one required for you'})</span>
      <select data-scopef-products multiple size="7" style="width:100%;">${scopeProductOptions(s.product_config_ids)}</select></label>
    <label>Lead paragraph <textarea data-scopef="lead" rows="2">${escapeHtml(s.lead || '')}</textarea></label>
    <label>Bullets (one per line, "label | text")<textarea data-scopef="bullets" rows="3">${escapeHtml(bullets)}</textarea></label>
    <div><button class="btn primary" data-scope-save>${editing ? 'Save changes' : 'Add scope'}</button>
      ${editing ? '<button class="btn" data-scope-cancel>Cancel</button>' : ''}</div>
  </div>`;
}

function wireConfig() {
  const el = Cfg.container;
  el.querySelector('[data-cfg-logo]')?.addEventListener('change', onLogoPick);
  el.addEventListener('click', onConfigClick);
}

async function onLogoPick(ev) {
  const file = ev.target.files && ev.target.files[0];
  if (!file) return;
  const st = Cfg.container.querySelector('[data-role="logo-state"]');
  if (st) st.textContent = 'uploading…';
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.fetch(API.crmQuoteSettingsLogo(), { method: 'POST', body: fd });
    if (st) st.textContent = res && res.sink === 'db' ? 'logo stored (db fallback)' : 'logo stored';
    flash('Logo uploaded.', 'success');
  } catch (err) {
    if (st) st.textContent = 'upload failed';
    flash((api.formatErrorDetail && api.formatErrorDetail(err)) || 'Logo upload failed', 'error');
  } finally {
    ev.target.value = '';
  }
}

async function onConfigClick(ev) {
  const t = ev.target;
  if (t.closest('[data-cfg-save]')) return saveSettings();
  // Capture edits (syncTerms) BEFORE mutating the array, then render — never the
  // reverse, or reading the stale DOM by index would undo the mutation.
  if (t.closest('[data-term-add]')) { syncTerms(); getTerms().push({ title: '', body: '' }); return renderTermsList(); }
  const termEl = t.closest('[data-ti]');
  if (termEl) {
    const i = +termEl.dataset.ti;
    if (t.closest('[data-term-del]')) { syncTerms(); getTerms().splice(i, 1); return renderTermsList(); }
    if (t.closest('[data-term-up]') && i > 0) { syncTerms(); const terms = getTerms(); [terms[i - 1], terms[i]] = [terms[i], terms[i - 1]]; return renderTermsList(); }
    const terms = getTerms();
    if (t.closest('[data-term-down]') && i < terms.length - 1) { syncTerms(); [terms[i + 1], terms[i]] = [terms[i], terms[i + 1]]; return renderTermsList(); }
  }
  const scopeRow = t.closest('tr[data-sid]');
  if (scopeRow) {
    const sid = scopeRow.dataset.sid;
    if (t.closest('[data-scope-edit]')) { Cfg.scopeEditId = sid; return refreshScopeForm(); }
    if (t.closest('[data-scope-deactivate]')) return patchScope(sid, { is_active: false });
    if (t.closest('[data-scope-activate]')) return patchScope(sid, { is_active: true });
  }
  if (t.closest('[data-scope-cancel]')) { Cfg.scopeEditId = null; return refreshScopeForm(); }
  if (t.closest('[data-scope-save]')) return saveScope();
}

function getTerms() { Cfg.settings.terms = Cfg.settings.terms || []; return Cfg.settings.terms; }

// Read the current inputs back into the model. Run this BEFORE any structural
// change (add/delete/reorder) or before saving — never after a mutation, since
// it maps stale DOM rows to array indices and would clobber the change.
function syncTerms() {
  const terms = getTerms();
  Cfg.container.querySelectorAll('[data-ti]').forEach((row) => {
    const i = +row.dataset.ti;
    const types = Array.from(row.querySelectorAll('[data-term-ctype]:checked')).map((el) => el.dataset.termCtype);
    terms[i] = {
      title: row.querySelector('[data-term-field="title"]').value,
      body: row.querySelector('[data-term-field="body"]').value,
      customer_types: types,
    };
  });
}

function renderTermsList() {
  Cfg.container.querySelector('[data-role="cfg-terms"]').innerHTML = renderTerms(getTerms());
}
function refreshScopeForm() { Cfg.container.querySelector('[data-role="cfg-scope-form"]').innerHTML = renderScopeForm(); }

async function saveSettings() {
  const el = Cfg.container;
  const val = (k) => { const n = el.querySelector(`[data-cfg="${k}"]`); return n ? n.value : undefined; };
  syncTerms();
  const payload = {
    letterhead: {
      name: val('lh.name'), website: val('lh.website'), phone_label: val('lh.phone_label'),
      phone: val('lh.phone'), email: val('lh.email'),
      address_lines: (val('lh.address_lines') || '').split('\n').map((x) => x.trim()).filter(Boolean),
    },
    footer_line: val('footer_line'),
    validity_days: Number(val('validity_days')) || 30,
    number_prefix: val('number_prefix'),
    default_notes: (val('default_notes') || '').split('\n').map((x) => x.trim()).filter(Boolean),
    office_email: val('office_email') || null,
    office_phone: val('office_phone') || null,
    terms: getTerms(),
  };
  try {
    Cfg.settings = await api.fetch(API.crmQuoteSettings(), { method: 'PUT', body: JSON.stringify(payload) });
    flash('Quotation settings saved.', 'success');
  } catch (err) { flash(api.formatErrorDetail(err) || 'Save failed', 'error'); }
}

function scopePayload() {
  const el = Cfg.container;
  const v = (k) => { const n = el.querySelector(`[data-scopef="${k}"]`); return n ? n.value : ''; };
  const bullets = (v('bullets') || '').split('\n').map((line) => {
    const [label, ...rest] = line.split('|');
    return rest.length ? { label: label.trim(), text: rest.join('|').trim() } : { label: '', text: label.trim() };
  }).filter((b) => b.text);
  const productSel = el.querySelector('[data-scopef-products]');
  const product_config_ids = productSel ? Array.from(productSel.selectedOptions).map((o) => o.value) : [];
  return {
    name: v('name').trim(), customer_type: v('customer_type') || null,
    product_config_ids, lead: v('lead') || null, bullets,
  };
}

async function saveScope() {
  const payload = scopePayload();
  if (!payload.name) { flash('Name is required', 'error'); return; }
  try {
    if (Cfg.scopeEditId) await api.fetch(API.crmQuoteScope(Cfg.scopeEditId), { method: 'PATCH', body: JSON.stringify(payload) });
    else await api.fetch(API.crmQuoteScopes(), { method: 'POST', body: JSON.stringify(payload) });
    flash('Scope saved.', 'success');
    Cfg.scopeEditId = null;
    await reloadScopes();
  } catch (err) {
    const code = err && err.body && err.body.detail;
    if (code === 'product_less_scope_requires_root') flash('Only root may create a scope without a product.', 'error');
    else flash(api.formatErrorDetail(err) || 'Save failed', 'error');
  }
}

async function patchScope(sid, patch) {
  try { await api.fetch(API.crmQuoteScope(sid), { method: 'PATCH', body: JSON.stringify(patch) }); await reloadScopes(); }
  catch (err) { flash(api.formatErrorDetail(err) || 'Update failed', 'error'); }
}

async function reloadScopes() {
  const data = await api.fetch(API.crmQuoteScopes(), { method: 'GET' }).catch(() => ({ items: [] }));
  Cfg.scopes = data.items || [];
  Cfg.container.querySelector('[data-role="cfg-scopes"]').innerHTML = renderScopes(Cfg.scopes);
  refreshScopeForm();
}

// --------------------------------------------------------------------------
// Standalone quotes modal — for the Acquisition Board, which has no row drawer.
// Hosts the same list + New-quote UI as mountQuotesSection, inside a dialog.
// The dialog is created lazily and reused across prospects.
// --------------------------------------------------------------------------
let quotesDlg = null;

export function openQuotesDialog(prospect) {
  if (!quotesDlg) {
    quotesDlg = document.createElement('dialog');
    quotesDlg.id = 'crmQuotesModal';
    quotesDlg.style.cssText = 'min-width:480px; max-width:90vw;';
    quotesDlg.innerHTML = `
      <div class="dlg-head" style="display:flex; justify-content:space-between; align-items:center;">
        <strong data-role="qm-title">Quotations</strong>
        <button type="button" class="btn ghost" data-role="qm-close" aria-label="Close quotations">✕</button>
      </div>
      <div class="dlg-body" data-role="qm-body" style="min-width:440px;"></div>`;
    document.body.appendChild(quotesDlg);
    quotesDlg.querySelector('[data-role="qm-close"]').addEventListener('click', () => closeDialog(quotesDlg));
  }
  quotesDlg.querySelector('[data-role="qm-title"]').textContent = prospect.full_name || 'Prospect';
  const body = quotesDlg.querySelector('[data-role="qm-body"]');
  body.innerHTML = '<div class="crm-detail-loading">Loading quotes…</div>';
  openDialog(quotesDlg);
  mountQuotesSection(body, prospect);
}

export default { mountQuotesSection, mountInbox, refreshInboxBadge, mountConfig, openQuotesDialog };
